import React, { useState, useEffect, useRef } from "react";
import { useField } from "formik";
import { TextField } from "@material-ui/core";

/**
 * A notes/text field that updates local state immediately (fast typing)
 * and only pushes to Formik after the user stops typing for `delay` ms.
 * This prevents all form sections from re-rendering on every keystroke.
 *
 * Recent-values dropdown (opt-in): when `remember` resolves to a key, the field
 * keeps a per-key list of the distinct values you've entered before (in
 * localStorage) and shows a small click-to-fill dropdown on focus. It is purely
 * additive — the input itself types/behaves exactly as before, and the dropdown
 * stays invisible until you've actually entered values. Keyboard: ↑/↓ highlight,
 * Enter/Tab accept the highlighted item, Esc closes. When nothing is highlighted,
 * keys behave normally (Enter still newlines, Tab still leaves the field).
 * `remember` resolution:
 *   remember === true | "someKey"  -> on (key = name or the string)
 *   remember === false             -> off
 *   remember === undefined         -> AUTO on for fields whose name ends in "notes"
 * so all the *_notes fields get it with no wiring, and any field can opt in/out.
 */
const RECENT_MAX = 10;
const recentKey = (k) => `recentValues:${k}`;
function readRecent(k) {
  try {
    const v = JSON.parse(localStorage.getItem(recentKey(k)));
    return Array.isArray(v) ? v : [];
  } catch {
    return [];
  }
}
function saveRecent(k, value) {
  const v = (value ?? "").toString().trim();
  if (!v) return;
  const list = [v, ...readRecent(k).filter((x) => x !== v)].slice(0, RECENT_MAX);
  try {
    localStorage.setItem(recentKey(k), JSON.stringify(list));
  } catch {
    /* localStorage full/unavailable — silently skip */
  }
}

export function DebouncedTextField({
  name,
  delay = 400,
  onBlur: externalOnBlur = undefined,
  remember = undefined,
  ...props
}) {
  const [field, , helpers] = useField(name);
  const [localValue, setLocalValue] = useState(field.value ?? "");
  const timerRef = useRef(null);
  const isTypingRef = useRef(false);
  // Mirrors the current value synchronously so blur commits the picked/typed value
  // even inside the same event tick (avoids a stale-closure overwrite on Tab-select).
  const valueRef = useRef(field.value ?? "");

  // Resolve whether/what to remember for this field.
  const rememberKey =
    remember === false
      ? null
      : remember === true
      ? name
      : typeof remember === "string" && remember
      ? remember
      : /notes$/i.test(name)
      ? name
      : null;

  const [open, setOpen] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [highlight, setHighlight] = useState(-1);
  const blurTimerRef = useRef(null);

  // Sync Formik → local only when an external change updates the Formik value
  // (e.g., form reset, or another field's useEffect calls setFieldValue on this field).
  // Skip sync while the user is actively typing to avoid overwriting mid-input.
  useEffect(() => {
    if (!isTypingRef.current) {
      setLocalValue(field.value ?? "");
      valueRef.current = field.value ?? "";
    }
  }, [field.value]);

  // Cleanup timers on unmount
  useEffect(() => {
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      if (blurTimerRef.current) clearTimeout(blurTimerRef.current);
    };
  }, []);

  const refreshSuggestions = (text) => {
    if (!rememberKey) return;
    const q = (text ?? "").trim().toLowerCase();
    const all = readRecent(rememberKey);
    setSuggestions(
      q ? all.filter((s) => s.toLowerCase().includes(q) && s.toLowerCase() !== q) : all,
    );
    setHighlight(-1);
  };

  const handleChange = (e) => {
    const val = e.target.value;
    setLocalValue(val);
    valueRef.current = val;
    isTypingRef.current = true;
    if (rememberKey) {
      refreshSuggestions(val);
      if (!open) setOpen(true);
    }
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      helpers.setValue(val);
      isTypingRef.current = false;
    }, delay);
  };

  const commit = (val) => {
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = null;
    isTypingRef.current = false;
    helpers.setValue(val);
    helpers.setTouched(true);
    if (rememberKey) saveRecent(rememberKey, val);
  };

  const handleBlur = (e) => {
    // Delay closing so a mousedown on a suggestion registers first.
    if (rememberKey) {
      blurTimerRef.current = setTimeout(() => setOpen(false), 150);
    }
    commit(valueRef.current);
    if (externalOnBlur) externalOnBlur(e);
  };

  const handleFocus = (e) => {
    if (rememberKey) {
      refreshSuggestions(localValue);
      setOpen(true);
    }
    if (props.onFocus) props.onFocus(e);
  };

  const pickSuggestion = (s) => {
    if (blurTimerRef.current) clearTimeout(blurTimerRef.current);
    setLocalValue(s);
    valueRef.current = s;
    setOpen(false);
    setHighlight(-1);
    commit(s);
  };

  const handleKeyDown = (e) => {
    if (props.onKeyDown) props.onKeyDown(e);
    if (!rememberKey) return;

    // Open the list on ArrowDown even if it was closed.
    if (e.key === "ArrowDown" && (!open || suggestions.length === 0)) {
      const all = readRecent(rememberKey);
      const q = (localValue ?? "").trim().toLowerCase();
      const next = q ? all.filter((s) => s.toLowerCase().includes(q) && s.toLowerCase() !== q) : all;
      if (next.length) {
        e.preventDefault();
        setSuggestions(next);
        setOpen(true);
        setHighlight(0);
      }
      return;
    }
    if (!open || suggestions.length === 0) return;

    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlight((h) => Math.min(h + 1, suggestions.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlight((h) => Math.max(h - 1, 0));
    } else if (e.key === "Enter") {
      if (highlight >= 0 && highlight < suggestions.length) {
        e.preventDefault(); // don't submit / newline when accepting a suggestion
        pickSuggestion(suggestions[highlight]);
      }
    } else if (e.key === "Tab") {
      // Accept the highlighted item and let Tab continue to the next field.
      if (highlight >= 0 && highlight < suggestions.length) {
        pickSuggestion(suggestions[highlight]);
      } else {
        setOpen(false);
      }
    } else if (e.key === "Escape") {
      setOpen(false);
      setHighlight(-1);
    }
  };

  const input = (
    <TextField
      {...props}
      name={name}
      value={localValue}
      onChange={handleChange}
      onBlur={handleBlur}
      onFocus={handleFocus}
      onKeyDown={handleKeyDown}
    />
  );

  // Non-remember fields render exactly as before — no wrapper, no dropdown.
  if (!rememberKey) return input;

  return (
    <div style={{ position: "relative", width: "100%" }}>
      {input}
      {open && suggestions.length > 0 && (
        <ul
          style={{
            position: "absolute",
            top: "100%",
            left: 0,
            right: 0,
            zIndex: 40,
            margin: 0,
            padding: 0,
            listStyle: "none",
            background: "#fff",
            border: "1px solid #ccc",
            borderRadius: 3,
            maxHeight: 180,
            overflowY: "auto",
            boxShadow: "0 2px 6px rgba(0,0,0,0.15)",
          }}
        >
          {suggestions.map((s, i) => (
            <li
              key={i}
              // onMouseDown (not onClick) + preventDefault so the pick fires before
              // the input's blur, and blur doesn't steal the click.
              onMouseDown={(e) => {
                e.preventDefault();
                pickSuggestion(s);
              }}
              onMouseEnter={() => setHighlight(i)}
              style={{
                padding: "4px 8px",
                fontSize: 12,
                cursor: "pointer",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
                background: i === highlight ? "#e3f0ff" : "#fff",
                borderBottom: i < suggestions.length - 1 ? "1px solid #eee" : "none",
              }}
              title={s}
            >
              {s}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
